import { z } from "zod";
import type { FinancialYear } from "../types.js";

// ---------------------------------------------------------------------------
// default-super-insurance — the "default super insurance you may be silently
// paying for" AWARENESS checker. From coarse, non-identifying inputs (age /
// balance band / inactivity / multiple-account) it tells the user WHICH
// default-cover rules likely apply to an account like theirs:
//
//   - insurance is OFF by default UNDER AGE 25 (PMIF, opt-out unless elected)
//   - insurance is OFF by default UNDER $6,000 balance (PMIF)
//   - insurance is SWITCHED OFF after 16 MONTHS of inactivity (PYS)
//   - and flags the DUPLICATE-PREMIUM-ACROSS-ACCOUNTS trap (typical-case).
//
// This is NOT a per-product ruleset like au06/au08 — it is not one of the
// PRODUCTS codes — so its rulesets carry an `engine: "default-super-insurance"`
// tag plus the usual provenance fields (mirroring the super-boost engine).
// Calculation is pure: inputs -> rules -> outputs; the assessment date is always
// passed in, no Date.now(), no I/O.
//
// HARD RULE (binding, panel §2 + figure ledger #6): default life / TPD /
// income-protection cover inside super IS a Corporations Act s764A financial
// product with NO s765A carve-out (the carve-out is health-insurance-specific).
// Therefore:
//   * NO personalised dollar number, premium, or coverage amount may be emitted.
//   * NO super-fund / super-product / SMSF / USI / SPIN identifier and NO
//     keep/cancel/buy/switch handoff may reach the output.
// Both are encoded as guards (`assertNoProductHandoff`) run over every emitted
// output object and asserted in the tests — $0 affiliate by design. The output
// is RULE-EXPLANATION (which rules apply + the cautions), never advice.
// ---------------------------------------------------------------------------

/** A complete default-super-insurance ruleset, keyed by financial year. */
export interface DefaultSuperInsuranceRuleSet {
  /** Engine tag — distinguishes shared engines from per-product rulesets. */
  engine: "default-super-insurance";
  /** Financial-year key, e.g. "2025-26". */
  fy: FinancialYear;
  /** Inclusive FY start (1 July). */
  effectiveFrom: string;
  /** Inclusive FY end (30 June). */
  effectiveTo: string;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;

  // --- PMIF (Putting Members' Interests First) opt-out triggers ---
  /**
   * Minimum age at which default (opt-out) insurance is provided. Under this
   * age, cover is OFF unless the member elects in. PMIF: age 25.
   */
  minAgeForDefaultCover: number;
  /**
   * Minimum account balance at which default (opt-out) insurance is provided.
   * Below this, cover is OFF unless the member elects in. PMIF: $6,000. The
   * trigger is "balance has reached $6,000 at any time", expressed here as the
   * threshold the entered balance band is tested against.
   */
  minBalanceForDefaultCover: number;
  /**
   * Months of inactivity (no contributions/rollovers received) after which a
   * trustee must SWITCH OFF insurance unless the member elects to keep it.
   * PYS (Protecting Your Super): 16 months.
   */
  inactiveMonthsCancelThreshold: number;
}

// ---------------------------------------------------------------------------
// input
// ---------------------------------------------------------------------------

/**
 * Coarse balance bands — NON-IDENTIFYING by design (panel build-condition 4 /
 * privacy §H1). The user never enters an exact dollar balance or a fund name;
 * they pick the band their account sits in. The only balance fact the rules
 * need is "is it at or above $6,000".
 *
 *   under6k       — below the $6,000 PMIF threshold
 *   sixToFiftyK   — $6,000 to under $50,000 (at/above the threshold)
 *   fiftyKPlus    — $50,000 and over (at/above the threshold)
 *   unknown       — the user doesn't know; we explain the rule without asserting
 */
export const BalanceBandSchema = z.enum([
  "under6k",
  "sixToFiftyK",
  "fiftyKPlus",
  "unknown",
]);
export type BalanceBand = z.infer<typeof BalanceBandSchema>;

export const DefaultSuperInsuranceInputSchema = z.object({
  /** Age in whole years (coarse). Tested against the age-25 PMIF trigger. */
  age: z.number().int().min(0).max(130),
  /** Coarse balance band — never an exact balance or a fund name. */
  balanceBand: BalanceBandSchema,
  /**
   * Months since the account last received a contribution or rollover. Tested
   * against the 16-month PYS inactivity trigger. `null` = the user doesn't know.
   */
  inactiveMonths: z.number().int().min(0).max(600).nullable().default(null),
  /**
   * Number of separate active super accounts the user holds. ≥2 raises the
   * duplicate-premium caution (typical-case, conditional — never an assertion
   * about THIS user). Defaults to 1 (no duplication claim).
   */
  activeAccountCount: z.number().int().min(0).max(50).default(1),
  /**
   * The financial year being assessed, e.g. "2025-26". Passed in (never derived
   * from the clock) so historical re-runs resolve against the rules in force.
   */
  assessmentYear: z.string(),
});
export type DefaultSuperInsuranceInput = z.input<
  typeof DefaultSuperInsuranceInputSchema
>;

// ---------------------------------------------------------------------------
// output — RULE-EXPLANATION ONLY. No dollar number, no coverage amount, no
// product identifier, no keep/cancel/buy/switch steer. Every field is a boolean
// or a stable enum key describing WHICH rule applies and WHY.
// ---------------------------------------------------------------------------

/**
 * Why default cover is (likely) OFF for an account like the one described. A
 * set of statutory reasons; empty when none of the opt-out triggers apply.
 */
export type DefaultCoverOffReason = "under25" | "balanceUnder6k" | "inactive16Months";

export interface DefaultSuperInsuranceOutput {
  // --- the three statutory triggers, as plain booleans ---
  /** Account holder is under the PMIF default-cover age (25). */
  under25: boolean;
  /** Balance band is below the PMIF $6,000 default-cover threshold. */
  balanceUnder6k: boolean;
  /**
   * Inactive for at least the PYS cancellation threshold (16 months). `null`
   * when inactivity wasn't supplied — the rule is explained but not asserted.
   */
  inactive16Months: boolean | null;

  /**
   * Whether, on the rules alone, default (opt-out) insurance is LIKELY OFF for
   * an account like this — true if any opt-out trigger fires. This is a
   * statement about how the RULES apply to the described account, NOT a finding
   * that THIS user has or lacks cover (they may have elected in).
   */
  defaultCoverLikelyOff: boolean;
  /** Which statutory rules drive `defaultCoverLikelyOff` (stable keys). */
  reasons: readonly DefaultCoverOffReason[];

  // --- the duplicate-premium wedge — typical-case, conditional ---
  /**
   * The user reported 2+ active accounts, so the duplicate-premium caution
   * applies (typical-case). Never asserts the user IS paying duplicate premiums.
   */
  multipleAccounts: boolean;

  /**
   * True when none of the opt-out triggers fire AND inactivity was supplied —
   * i.e. an account like this would, by default, have cover switched ON (still
   * not a claim that THIS account does). Helps the copy say "default cover
   * generally applies to an account like this" without a number.
   */
  defaultCoverGenerallyApplies: boolean;
}
